// NeuroRehab AI — deterministic engine + localStorage store.
// SAFETY: no LLM invents treatment plans. All exercise selection and
// difficulty progression is rule-based here.

const KEY = "silentscan_neurorehab_v1";

export const CATEGORIES = [
  { key: "memory", label: "Memory", accent: "#a78bfa" },
  { key: "attention", label: "Attention", accent: "#22d3ee" },
  { key: "speed", label: "Processing Speed", accent: "#fbbf24" },
  { key: "problem", label: "Problem Solving", accent: "#34d399" },
  { key: "executive", label: "Executive Function", accent: "#f87171" },
];

const DEFAULT = {
  profile: {
    consent_stored: false,
    accessibility: {
      large_text: false,
      high_contrast: false,
      audio_cues: false,
    },
    caregiver_share_code: null,
  },
  baseline: null, // { at, results: { memory: { accuracy, reaction_ms }, ... } }
  levels: {
    memory: 1,
    attention: 1,
    speed: 1,
    problem: 1,
    executive: 1,
  },
  sessions: [], // { at, category, level, correct, total, reaction_ms }
  functional_sessions: [], // { at, kind, score, total, notes }
  feedback: [], // { at, category, level, mood, difficulty, energy }
};

export const loadNeuro = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : { ...DEFAULT };
  } catch {
    return { ...DEFAULT };
  }
};

export const saveNeuro = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    // storage full
  }
};

export const clearNeuro = () => {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    // ignore
  }
};

// Adaptive difficulty engine
// Rules (safety: no LLM):
//   accuracy >= 85%  -> level up (max 5)
//   accuracy < 50%   -> level down (min 1)
//   otherwise        -> stay
export const nextLevel = (currentLevel, correct, total) => {
  const acc = total > 0 ? correct / total : 0;
  if (acc >= 0.85) return Math.min(5, currentLevel + 1);
  if (acc < 0.5) return Math.max(1, currentLevel - 1);
  return currentLevel;
};

// Store a completed exercise result and adapt level.
export const recordExercise = (category, level, correct, total, reactionMs) => {
  const state = loadNeuro();
  state.sessions = [
    ...(state.sessions || []).slice(-499),
    {
      at: new Date().toISOString(),
      category,
      level,
      correct: Number(correct),
      total: Number(total),
      reaction_ms: Math.round(reactionMs || 0),
    },
  ];
  state.levels = state.levels || { ...DEFAULT.levels };
  state.levels[category] = nextLevel(level, correct, total);
  saveNeuro(state);
  return state;
};

export const recordBaseline = (results) => {
  const state = loadNeuro();
  state.baseline = { at: new Date().toISOString(), results };
  // Seed levels from baseline accuracy — decent starting point
  Object.entries(results || {}).forEach(([cat, r]) => {
    const acc = r?.total ? (r.correct || 0) / r.total : 0;
    state.levels[cat] = acc >= 0.85 ? 3 : acc >= 0.6 ? 2 : 1;
  });
  saveNeuro(state);
  return state;
};

// Deterministic 20-minute training plan.
// Rotates through all 5 categories with allotted times based on the
// user's current level (higher level = shorter time slot).
export const buildDailyPlan = (state) => {
  const s = state || loadNeuro();
  const now = new Date();
  const dayIndex = now.getDate();
  // Order rotates day to day so no category dominates
  const rotated = [...CATEGORIES.map((c) => c.key)];
  for (let i = 0; i < dayIndex % rotated.length; i += 1) {
    rotated.push(rotated.shift());
  }
  return rotated.slice(0, 4).map((cat) => ({
    category: cat,
    level: (s.levels && s.levels[cat]) || 1,
    minutes: 5,
  }));
};

// Store a completed FUNCTIONAL (real-life) exercise result.
export const recordFunctional = (kind, score, total, notes = "") => {
  const state = loadNeuro();
  state.functional_sessions = [
    ...(state.functional_sessions || []).slice(-499),
    {
      at: new Date().toISOString(),
      kind,
      score: Number(score),
      total: Number(total),
      notes: String(notes || ""),
    },
  ];
  saveNeuro(state);
  return state;
};

// Store post-session feedback (quick 3-tap: mood/difficulty/energy).
export const recordFeedback = (category, level, feedback) => {
  const state = loadNeuro();
  state.feedback = [
    ...(state.feedback || []).slice(-499),
    {
      at: new Date().toISOString(),
      category,
      level,
      mood: feedback.mood || null, // 1..5
      difficulty: feedback.difficulty || null, // 1..5
      energy: feedback.energy || null, // 1..5
    },
  ];
  saveNeuro(state);
  return state;
};

// Before-vs-After comparator.
// Compares baseline accuracy per category against average accuracy over the
// most recent `windowDays` days for the same category. Non-diagnostic.
export const beforeAfter = (state, windowDays = 7) => {
  const s = state || loadNeuro();
  const baseline = s.baseline?.results || {};
  const cutoff = Date.now() - windowDays * 24 * 3600 * 1000;
  const recent = (s.sessions || []).filter(
    (x) => new Date(x.at || 0).getTime() >= cutoff
  );
  return CATEGORIES.map((c) => {
    const b = baseline[c.key];
    const beforePct = b?.total ? Math.round((b.correct / b.total) * 100) : 0;
    const list = recent.filter((x) => x.category === c.key);
    const ex = list.reduce((a, x) => a + (x.total || 0), 0);
    const co = list.reduce((a, x) => a + (x.correct || 0), 0);
    const afterPct = ex ? Math.round((co / ex) * 100) : 0;
    return {
      key: c.key,
      label: c.label,
      accent: c.accent,
      before: beforePct,
      after: afterPct,
      delta: afterPct - beforePct,
      sample: ex,
    };
  });
};

// Recovery Twin: longitudinal daily aggregate for the last N days.
// Combines cognitive accuracy, functional-session score and feedback mood.
export const recoveryTimeline = (state, days = 14) => {
  const s = state || loadNeuro();
  const now = new Date();
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const cog = (s.sessions || []).filter((x) => (x.at || "").slice(0, 10) === key);
    const fun = (s.functional_sessions || []).filter(
      (x) => (x.at || "").slice(0, 10) === key
    );
    const fbk = (s.feedback || []).filter((x) => (x.at || "").slice(0, 10) === key);
    const ex = cog.reduce((a, x) => a + (x.total || 0), 0);
    const co = cog.reduce((a, x) => a + (x.correct || 0), 0);
    const cognitive = ex ? Math.round((co / ex) * 100) : 0;
    const fEx = fun.reduce((a, x) => a + (x.total || 0), 0);
    const fCo = fun.reduce((a, x) => a + (x.score || 0), 0);
    const functional = fEx ? Math.round((fCo / fEx) * 100) : 0;
    const moodSum = fbk.reduce((a, x) => a + (x.mood || 0), 0);
    const mood = fbk.length ? Math.round((moodSum / fbk.length) * 20) : 0;
    out.push({
      day: key.slice(5),
      cognitive,
      functional,
      mood,
      sessions: cog.length + fun.length,
    });
  }
  return out;
};

// Aggregate helpers for the progress dashboard.
export const aggregate = (state) => {
  const s = state || loadNeuro();
  const sessions = s.sessions || [];
  const total = sessions.length;
  const exercisesTotal = sessions.reduce((a, b) => a + (b.total || 0), 0);
  const correctTotal = sessions.reduce((a, b) => a + (b.correct || 0), 0);
  const accuracy = exercisesTotal ? Math.round((correctTotal / exercisesTotal) * 100) : 0;
  const reactionMs = sessions.length
    ? Math.round(
        sessions.filter((x) => x.reaction_ms > 0).reduce((a, b) => a + b.reaction_ms, 0) /
          Math.max(1, sessions.filter((x) => x.reaction_ms > 0).length)
      )
    : 0;
  const perCat = {};
  CATEGORIES.forEach((c) => {
    const list = sessions.filter((x) => x.category === c.key);
    const ex = list.reduce((a, b) => a + (b.total || 0), 0);
    const co = list.reduce((a, b) => a + (b.correct || 0), 0);
    perCat[c.key] = ex ? Math.round((co / ex) * 100) : 0;
  });
  const personalBest = sessions.length
    ? Math.max(...sessions.map((x) => (x.total ? Math.round((x.correct / x.total) * 100) : 0)))
    : 0;

  // Consistency = (# distinct days practised in last 14) / 14
  const days = new Set(sessions.map((x) => (x.at || "").slice(0, 10))).size;
  const consistency = Math.min(100, Math.round((days / 14) * 100));

  return {
    total_sessions: total,
    exercises_total: exercisesTotal,
    accuracy,
    reaction_ms: reactionMs,
    per_category: perCat,
    personal_best: personalBest,
    consistency,
  };
};

// Weekly rolling accuracy series (for line chart).
export const weeklyTrend = (state, weeks = 6) => {
  const s = state || loadNeuro();
  const now = new Date();
  const buckets = [];
  for (let i = weeks - 1; i >= 0; i -= 1) {
    const end = new Date(now);
    end.setDate(now.getDate() - i * 7);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    const list = (s.sessions || []).filter((x) => {
      const t = new Date(x.at || "").getTime();
      return t >= start.getTime() && t <= end.getTime();
    });
    const ex = list.reduce((a, b) => a + (b.total || 0), 0);
    const co = list.reduce((a, b) => a + (b.correct || 0), 0);
    buckets.push({
      week: `W${weeks - i}`,
      accuracy: ex ? Math.round((co / ex) * 100) : 0,
    });
  }
  return buckets;
};

// Demo mode data — clearly labeled as simulated.
export const buildDemoState = () => {
  const now = Date.now();
  const sessions = [];
  const cats = CATEGORIES.map((c) => c.key);
  for (let d = 27; d >= 0; d -= 1) {
    const ts = new Date(now - d * 24 * 3600 * 1000);
    const cat = cats[d % cats.length];
    const level = Math.min(5, Math.max(1, 1 + Math.floor((27 - d) / 6)));
    // Simulated learning curve: accuracy improves from ~55% to ~90%
    const baseAcc = 0.55 + ((27 - d) / 27) * 0.35;
    const total = 10;
    const correct = Math.round(baseAcc * total);
    const reaction = Math.round(1400 - ((27 - d) / 27) * 500);
    sessions.push({
      at: ts.toISOString(),
      category: cat,
      level,
      correct,
      total,
      reaction_ms: reaction,
    });
  }
  const functional_sessions = [];
  const feedback = [];
  const kinds = ["shopping", "medication", "scheduling"];
  for (let d = 20; d >= 0; d -= 3) {
    const ts = new Date(now - d * 24 * 3600 * 1000);
    const kind = kinds[d % kinds.length];
    const total = 6;
    const score = Math.min(total, 3 + Math.round(((20 - d) / 20) * 3));
    functional_sessions.push({
      at: ts.toISOString(),
      kind,
      score,
      total,
      notes: "",
    });
    feedback.push({
      at: ts.toISOString(),
      category: kind,
      level: 2,
      mood: Math.min(5, 3 + Math.round((20 - d) / 20)),
      difficulty: Math.max(1, 4 - Math.round((20 - d) / 10)),
      energy: Math.min(5, 3 + Math.round((20 - d) / 20)),
    });
  }
  return {
    ...DEFAULT,
    baseline: {
      at: new Date(now - 28 * 24 * 3600 * 1000).toISOString(),
      results: {
        memory: { correct: 5, total: 10, reaction_ms: 1500 },
        attention: { correct: 6, total: 10, reaction_ms: 1400 },
        speed: { correct: 5, total: 10, reaction_ms: 1300 },
        problem: { correct: 4, total: 10, reaction_ms: 1600 },
        executive: { correct: 4, total: 10, reaction_ms: 1500 },
      },
    },
    levels: { memory: 4, attention: 4, speed: 3, problem: 3, executive: 3 },
    sessions,
    functional_sessions,
    feedback,
  };
};
